import { useCallback, useRef, useState } from 'react'

export function useCopilotPanelUi() {
  const [searchText, setSearchText] = useState('')
  const [expandedCardIds, setExpandedCardIds] = useState<string[]>([])
  const [expandedQuoteIds, setExpandedQuoteIds] = useState<string[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  /** Survive CopilotSidebar unmount so reopen does not re-seed a collapse the user made. */
  const seededExpandKeysRef = useRef(new Set<string>())
  const seededQuoteKeysRef = useRef(new Set<string>())

  const markCopilotOpened = useCallback(() => {
    setUnreadCount(0)
  }, [])

  const bumpUnread = useCallback((delta = 1) => {
    setUnreadCount((current) => current + delta)
  }, [])

  const toggleExpanded = useCallback((id: string) => {
    setExpandedCardIds((current) =>
      current.includes(id) ? current.filter((openId) => openId !== id) : [...current, id]
    )
  }, [])

  const ensureExpanded = useCallback((id: string) => {
    setExpandedCardIds((current) => (current.includes(id) ? current : [...current, id]))
  }, [])

  const toggleQuoteExpanded = useCallback((id: string) => {
    setExpandedQuoteIds((current) =>
      current.includes(id) ? current.filter((openId) => openId !== id) : [...current, id]
    )
  }, [])

  const ensureQuoteExpanded = useCallback((id: string) => {
    setExpandedQuoteIds((current) => (current.includes(id) ? current : [...current, id]))
  }, [])

  const seedAnswerExpanded = useCallback(
    (id: string) => {
      if (seededExpandKeysRef.current.has(id)) return
      seededExpandKeysRef.current.add(id)
      ensureExpanded(id)
    },
    [ensureExpanded]
  )

  const seedQuoteExpanded = useCallback(
    (id: string) => {
      if (seededQuoteKeysRef.current.has(id)) return
      seededQuoteKeysRef.current.add(id)
      ensureQuoteExpanded(id)
    },
    [ensureQuoteExpanded]
  )

  const clearExpandSeeds = useCallback(() => {
    seededExpandKeysRef.current.clear()
    seededQuoteKeysRef.current.clear()
  }, [])

  return {
    searchText,
    setSearchText,
    expandedCardIds,
    toggleExpanded,
    ensureExpanded,
    expandedQuoteIds,
    toggleQuoteExpanded,
    ensureQuoteExpanded,
    seedAnswerExpanded,
    seedQuoteExpanded,
    clearExpandSeeds,
    unreadCount,
    markCopilotOpened,
    bumpUnread,
  }
}

export type CopilotPanelUiState = ReturnType<typeof useCopilotPanelUi>
